import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Mail, MailOpen } from 'lucide-react';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_CATEGORY_LABELS,
  REACTION_FACES,
  type FeedbackCategory,
  type FeedbackStatus,
} from '@ready/shared';
import { useApi, type FeedbackItem, type FeedbackSummary, type InboxScope } from '@/lib/api';
import { formatDateTime } from '@/lib/dates';
import { useDebounced } from '@/lib/use-debounced';
import { Badge, Button, Card, ErrorText } from '@/components/ui';
import { SearchBox } from '@/components/catalog';
import { Filter, Pager } from '@/components/people';

/*
 * A feedback inbox (the platform's or an org's): what people said, newest and
 * unread first, with a 30-day summary. Admins mark items read, unread or
 * archived; parent-org admins (`canManage` false) only read.
 */

const PAGE = 25;
const VIEWS = [
  ['inbox', 'Inbox'],
  ['unread', 'Unread'],
  ['archived', 'Archived'],
] as const;

export function FeedbackInbox({ scope, canManage }: { scope: InboxScope; canManage: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const key = scope.kind === 'platform' ? 'platform' : scope.orgId;
  const [view, setView] = useState<(typeof VIEWS)[number][0]>('inbox');
  const [reaction, setReaction] = useState('');
  const [category, setCategory] = useState('');
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  const query = {
    view,
    reaction: reaction ? Number(reaction) : undefined,
    category: (category || undefined) as FeedbackCategory | undefined,
    q: q || undefined,
    offset: page * PAGE,
    limit: PAGE,
  };
  const list = useQuery({
    queryKey: ['feedback', key, query],
    queryFn: () => api.feedback(scope, query),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ['feedbackSummary', key],
    queryFn: () => api.feedbackSummary(scope),
  });
  const setStatus = useMutation({
    mutationFn: (v: { ids: string[]; status: FeedbackStatus }) =>
      api.setFeedbackStatus(scope, v.ids, v.status),
    onSuccess: () => {
      setPicked([]);
      queryClient.invalidateQueries({ queryKey: ['feedback', key] });
      queryClient.invalidateQueries({ queryKey: ['feedbackSummary', key] });
      queryClient.invalidateQueries({ queryKey: ['inboxCounts'] });
    },
  });
  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
    setPicked([]);
  };
  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <Summary data={summary.data} />
      <Card>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Show">
            {VIEWS.map(([v, label]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => refilter(() => setView(v))}
                className={`min-h-9 rounded-full border px-3 text-sm ${view === v ? 'border-primary bg-primary/15 text-primary-strong' : 'border-border hover:bg-muted'}`}
              >
                {label}
                {v === 'unread' && summary.data?.unread ? ` (${summary.data.unread})` : ''}
              </button>
            ))}
          </div>
          <SearchBox
            value={text}
            onChange={(v) => refilter(() => setText(v))}
            placeholder="Search what people wrote"
            label="Search feedback"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Filter
              id="fb-reaction"
              label="Reaction"
              value={reaction}
              onChange={(v) => refilter(() => setReaction(v))}
              options={[
                ['', 'Any reaction'],
                ...Object.entries(REACTION_FACES)
                  .reverse()
                  .map(([v, r]) => [v, `${r.face} ${r.label}`] as [string, string]),
              ]}
            />
            <Filter
              id="fb-category"
              label="About"
              value={category}
              onChange={(v) => refilter(() => setCategory(v))}
              options={[
                ['', 'Anything'],
                ...FEEDBACK_CATEGORIES.map(
                  (c) => [c, FEEDBACK_CATEGORY_LABELS[c]] as [string, string],
                ),
              ]}
            />
          </div>
        </div>

        {canManage && picked.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-2 text-sm">
            <span className="px-1">{picked.length} selected</span>
            <Button
              variant="secondary"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ ids: picked, status: 'read' })}
            >
              <MailOpen className="size-4" aria-hidden /> Mark read
            </Button>
            <Button
              variant="secondary"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ ids: picked, status: 'new' })}
            >
              <Mail className="size-4" aria-hidden /> Mark unread
            </Button>
            {view !== 'archived' && (
              <Button
                variant="ghost"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ ids: picked, status: 'archived' })}
              >
                <Archive className="size-4" aria-hidden /> Archive
              </Button>
            )}
          </div>
        )}
        <ErrorText error={list.error ?? setStatus.error} />

        <ul className="mt-4 divide-y divide-border">
          {list.isPending && <li className="py-3 text-sm text-muted-foreground">Loading…</li>}
          {list.isSuccess && items.length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">
              {view === 'unread' ? 'All caught up.' : 'No feedback here yet.'}
            </li>
          )}
          {items.map((item) => (
            <Row
              key={item.id}
              item={item}
              canManage={canManage}
              picked={picked.includes(item.id)}
              onPick={(on) =>
                setPicked((p) => (on ? [...p, item.id] : p.filter((id) => id !== item.id)))
              }
              onStatus={(status) => setStatus.mutate({ ids: [item.id], status })}
            />
          ))}
        </ul>
        <Pager
          page={page}
          pageSize={PAGE}
          total={list.data?.total ?? 0}
          busy={list.isFetching}
          onPage={setPage}
        />
      </Card>
    </div>
  );
}

function Row({
  item,
  canManage,
  picked,
  onPick,
  onStatus,
}: {
  item: FeedbackItem;
  canManage: boolean;
  picked: boolean;
  onPick: (on: boolean) => void;
  onStatus: (s: FeedbackStatus) => void;
}) {
  const unread = item.status === 'new';
  const face = item.reaction ? REACTION_FACES[item.reaction] : null;
  return (
    <li className="flex gap-3 py-3 text-sm">
      {canManage && (
        <input
          type="checkbox"
          aria-label="Select"
          className="mt-1 size-5 shrink-0 accent-primary"
          checked={picked}
          onChange={(e) => onPick(e.target.checked)}
        />
      )}
      <span
        className="w-8 shrink-0 text-2xl leading-none"
        title={face?.label}
        aria-label={face?.label}
      >
        {face?.face ?? '·'}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {unread && <span className="size-2 rounded-full bg-primary" aria-label="Unread" />}
          <span className={unread ? 'font-semibold' : 'font-medium'}>
            {item.sender ? (item.sender.name ?? item.sender.email) : 'Anonymous'}
          </span>
          {item.sender && item.anonymous && <Badge>Anonymous to the org</Badge>}
          {item.category && <Badge>{FEEDBACK_CATEGORY_LABELS[item.category]}</Badge>}
          <span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span>
        </div>
        {item.message && <p className="mt-1 whitespace-pre-wrap break-words">{item.message}</p>}
        {item.pageUrl && (
          <Link
            to={item.pageUrl}
            className="mt-1 inline-block font-mono text-xs text-muted-foreground hover:text-primary"
          >
            From {item.pageUrl}
          </Link>
        )}
      </div>
      {canManage && (
        <div className="flex shrink-0 items-start gap-1">
          <button
            type="button"
            title={unread ? 'Mark read' : 'Mark unread'}
            aria-label={unread ? 'Mark read' : 'Mark unread'}
            onClick={() => onStatus(unread ? 'read' : 'new')}
            className="flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            {unread ? (
              <MailOpen className="size-4" aria-hidden />
            ) : (
              <Mail className="size-4" aria-hidden />
            )}
          </button>
          {item.status !== 'archived' && (
            <button
              type="button"
              title="Archive"
              aria-label="Archive"
              onClick={() => onStatus('archived')}
              className="flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            >
              <Archive className="size-4" aria-hidden />
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function Summary({ data }: { data: FeedbackSummary | undefined }) {
  if (!data) return null;
  const avg = data.averageReaction;
  const prev = data.previousAverage;
  const trend = avg !== null && prev !== null ? Math.round((avg - prev) * 10) / 10 : null;
  const stats: [string, string, string?][] = [
    ['Unread', data.unread.toLocaleString('en-US')],
    ['Last 30 days', data.last30.toLocaleString('en-US')],
    [
      'Average reaction',
      avg === null ? '—' : `${REACTION_FACES[Math.round(avg)]?.face ?? ''} ${avg.toFixed(1)}`,
      trend === null ? undefined : `${trend >= 0 ? '+' : ''}${trend} vs the 30 days before`,
    ],
    [
      'Ideas · Problems · Praise',
      `${data.categories.idea} · ${data.categories.problem} · ${data.categories.praise}`,
    ],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {stats.map(([label, value, hint]) => (
        <Card key={label}>
          <p className="min-h-[2lh] text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </Card>
      ))}
    </div>
  );
}
